import { toast } from '@/components/ToastNotification'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { api } from '@/lib/api'
import { Loader2 } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import {
  GOOGLE_MEET_OAUTH_MESSAGE_TYPE,
  GOOGLE_MEET_OAUTH_STORAGE_KEY
} from './googleMeetOAuth'
import type { GoogleMeetConfigState } from './types'

const initialGoogleMeetConfig: GoogleMeetConfigState = {
  habilitado: false,
  client_id: '',
  client_secret: '',
  redirect_uri: '',
  email_conta_google: '',
  authCode: '',
  has_client_secret: false,
  has_refresh_token: false,
  token_expiry_date: null
}

interface ParametrosTabProps {
  getIdEmpresa: () => number | null
}

export function ParametrosTab({ getIdEmpresa }: ParametrosTabProps) {
  const [googleMeetConfig, setGoogleMeetConfig] =
    useState<GoogleMeetConfigState>(initialGoogleMeetConfig)
  const [loadingGoogleMeetConfig, setLoadingGoogleMeetConfig] = useState(false)
  const [savingGoogleMeetConfig, setSavingGoogleMeetConfig] = useState(false)
  const [generatingGoogleMeetAuthUrl, setGeneratingGoogleMeetAuthUrl] =
    useState(false)
  const [connectingGoogleMeet, setConnectingGoogleMeet] = useState(false)
  const oauthCodeProcessingRef = useRef(false)

  const loadGoogleMeetConfig = useCallback(async () => {
    const id = getIdEmpresa()
    if (!id) return
    setLoadingGoogleMeetConfig(true)
    try {
      const res = await api.get(`/api/empresas/${id}/google-meet-config`)
      const config = res?.result
      setGoogleMeetConfig(prev => ({
        ...prev,
        habilitado: config?.habilitado ?? false,
        client_id: config?.client_id ?? '',
        client_secret: '',
        redirect_uri: config?.redirect_uri ?? '',
        email_conta_google: config?.email_conta_google ?? '',
        has_client_secret: config?.has_client_secret ?? false,
        has_refresh_token: config?.has_refresh_token ?? false,
        token_expiry_date: config?.token_expiry_date ?? null
      }))
    } catch (_) {
      // Sem configuração salva ainda
    } finally {
      setLoadingGoogleMeetConfig(false)
    }
  }, [getIdEmpresa])

  useEffect(() => {
    loadGoogleMeetConfig()
  }, [loadGoogleMeetConfig])

  const exchangeGoogleMeetCode = useCallback(
    async (authorizationCode: string) => {
      const id = getIdEmpresa()
      if (!id) {
        toast.error('Empresa não identificada.')
        return
      }

      const code = authorizationCode.trim()
      if (!code) {
        toast.error('Informe o código de autorização retornado pelo Google.')
        return
      }

      if (oauthCodeProcessingRef.current) return
      oauthCodeProcessingRef.current = true
      setConnectingGoogleMeet(true)

      try {
        const response = await api.post(
          `/api/empresas/${id}/google-meet/exchange-code`,
          { code }
        )
        setGoogleMeetConfig(prev => ({
          ...prev,
          authCode: '',
          client_secret: ''
        }))
        await loadGoogleMeetConfig()
        toast.success(
          response?.result?.message || 'Conta Google conectada com sucesso.'
        )
      } catch (error: any) {
        toast.error(error?.message || 'Erro ao conectar conta Google.')
      } finally {
        setConnectingGoogleMeet(false)
        oauthCodeProcessingRef.current = false
      }
    },
    [getIdEmpresa, loadGoogleMeetConfig]
  )

  const consumeStoredGoogleMeetCode = useCallback((): string | null => {
    try {
      const raw = localStorage.getItem(GOOGLE_MEET_OAUTH_STORAGE_KEY)
      if (!raw) return null

      localStorage.removeItem(GOOGLE_MEET_OAUTH_STORAGE_KEY)

      const payload = JSON.parse(raw)
      if (!payload || typeof payload.code !== 'string') return null

      const receivedAt = Number(payload.receivedAt || 0)
      if (receivedAt && Date.now() - receivedAt > 10 * 60 * 1000) {
        return null
      }

      return payload.code
    } catch {
      return null
    }
  }, [])

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return

      const data = event.data
      if (!data || data.type !== GOOGLE_MEET_OAUTH_MESSAGE_TYPE) return
      if (typeof data.code !== 'string') return

      setGoogleMeetConfig(prev => ({
        ...prev,
        authCode: data.code
      }))
      void exchangeGoogleMeetCode(data.code)
    }

    window.addEventListener('message', handleMessage)
    return () => {
      window.removeEventListener('message', handleMessage)
    }
  }, [exchangeGoogleMeetCode])

  useEffect(() => {
    const tryConsumeStoredCode = () => {
      const storedCode = consumeStoredGoogleMeetCode()
      if (!storedCode) return

      setGoogleMeetConfig(prev => ({
        ...prev,
        authCode: storedCode
      }))
      void exchangeGoogleMeetCode(storedCode)
    }

    tryConsumeStoredCode()
    window.addEventListener('focus', tryConsumeStoredCode)

    return () => {
      window.removeEventListener('focus', tryConsumeStoredCode)
    }
  }, [consumeStoredGoogleMeetCode, exchangeGoogleMeetCode])

  const handleSaveGoogleMeetConfig = async () => {
    const id = getIdEmpresa()
    if (!id) {
      toast.error('Empresa não identificada.')
      return
    }
    if (googleMeetConfig.habilitado && !googleMeetConfig.has_refresh_token) {
      toast.error(
        'Conecte a conta Google primeiro para habilitar a integração automática.'
      )
      return
    }
    setSavingGoogleMeetConfig(true)
    try {
      await api.put(`/api/empresas/${id}/google-meet-config`, {
        habilitado: googleMeetConfig.habilitado,
        client_id: googleMeetConfig.client_id || undefined,
        client_secret: googleMeetConfig.client_secret || undefined,
        redirect_uri: googleMeetConfig.redirect_uri || undefined,
        email_conta_google: googleMeetConfig.email_conta_google || undefined
      })
      setGoogleMeetConfig(prev => ({ ...prev, client_secret: '' }))
      await loadGoogleMeetConfig()
      toast.success('Configuração do Google Meet salva com sucesso.')
    } catch (error: any) {
      toast.error(
        error?.message || 'Erro ao salvar configuração do Google Meet.'
      )
    } finally {
      setSavingGoogleMeetConfig(false)
    }
  }

  const handleGenerateGoogleMeetAuthUrl = async () => {
    const id = getIdEmpresa()
    if (!id) {
      toast.error('Empresa não identificada.')
      return
    }
    setGeneratingGoogleMeetAuthUrl(true)
    try {
      const response = await api.get(`/api/empresas/${id}/google-meet/auth-url`)
      const authUrl = response?.result?.authUrl
      if (!authUrl) throw new Error('Não foi possível gerar a URL de autorização.')
      window.open(authUrl, '_blank', 'noopener,noreferrer')
      toast.success(
        'URL de autorização gerada. Conclua o login no Google para finalizar a conexão.'
      )
    } catch (error: any) {
      toast.error(error?.message || 'Erro ao gerar URL de autorização.')
    } finally {
      setGeneratingGoogleMeetAuthUrl(false)
    }
  }

  const handleExchangeGoogleMeetCode = async () => {
    await exchangeGoogleMeetCode(googleMeetConfig.authCode)
  }

  return (
    <Card className="p-6">
      <div className="space-y-6">
        <div>
          <h3 className="font-semibold text-lg mb-4">
            Integração Google Meet
          </h3>
          <p className="text-sm text-muted-foreground mb-6">
            Configure as credenciais da API para criar links de reunião
            automaticamente nos agendamentos.
          </p>
        </div>

        {loadingGoogleMeetConfig ? (
          <div className="flex items-center gap-2 text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Carregando configuração...
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 border rounded-lg">
              <div className="space-y-0.5">
                <Label
                  htmlFor="videoconferencia"
                  className="text-base font-medium"
                >
                  Habilitar integração Google Meet
                </Label>
                <p className="text-sm text-muted-foreground">
                  Ativa a criação automática de links para tipos de atendimento
                  com Meet habilitado.
                </p>
              </div>
              <Switch
                id="videoconferencia"
                checked={googleMeetConfig.habilitado}
                onCheckedChange={checked => {
                  setGoogleMeetConfig(prev => ({ ...prev, habilitado: checked }))
                }}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="gm-client-id">Client ID</Label>
                <Input
                  id="gm-client-id"
                  value={googleMeetConfig.client_id}
                  onChange={e =>
                    setGoogleMeetConfig(prev => ({
                      ...prev,
                      client_id: e.target.value
                    }))
                  }
                  placeholder="Google OAuth Client ID"
                  autoComplete="off"
                />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="gm-client-secret">Client Secret</Label>
                <Input
                  id="gm-client-secret"
                  type="password"
                  value={googleMeetConfig.client_secret}
                  onChange={e =>
                    setGoogleMeetConfig(prev => ({
                      ...prev,
                      client_secret: e.target.value
                    }))
                  }
                  placeholder={
                    googleMeetConfig.has_client_secret
                      ? 'Já configurado. Informe apenas para alterar.'
                      : 'Google OAuth Client Secret'
                  }
                  autoComplete="off"
                />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="gm-redirect-uri">Redirect URI</Label>
                <Input
                  id="gm-redirect-uri"
                  value={googleMeetConfig.redirect_uri}
                  onChange={e =>
                    setGoogleMeetConfig(prev => ({
                      ...prev,
                      redirect_uri: e.target.value
                    }))
                  }
                  placeholder="http://localhost:3000/auth/callback"
                  autoComplete="off"
                />
                <p className="text-xs text-muted-foreground">
                  Exemplo: {`${window.location.origin}/google-meet/callback`}
                </p>
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="gm-email">E-mail da conta Google</Label>
                <Input
                  id="gm-email"
                  type="email"
                  value={googleMeetConfig.email_conta_google}
                  onChange={e =>
                    setGoogleMeetConfig(prev => ({
                      ...prev,
                      email_conta_google: e.target.value
                    }))
                  }
                  placeholder="conta@google.com"
                  autoComplete="off"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="p-3 border rounded-lg">
                <p className="text-xs text-muted-foreground mb-1">
                  Client Secret salvo
                </p>
                <p className="text-sm font-medium">
                  {googleMeetConfig.has_client_secret ? 'Sim' : 'Não'}
                </p>
              </div>
              <div className="p-3 border rounded-lg">
                <p className="text-xs text-muted-foreground mb-1">
                  Conta Google conectada (refresh token)
                </p>
                <p className="text-sm font-medium">
                  {googleMeetConfig.has_refresh_token ? 'Sim' : 'Não'}
                </p>
                {googleMeetConfig.token_expiry_date && (
                  <p className="text-xs text-muted-foreground mt-1">
                    Access token expira em:{' '}
                    {new Date(
                      Number(googleMeetConfig.token_expiry_date)
                    ).toLocaleString('pt-BR')}
                  </p>
                )}
              </div>
            </div>

            <div className="space-y-3 p-4 border rounded-lg">
              <div>
                <p className="text-sm font-medium">OAuth assistido</p>
                <p className="text-xs text-muted-foreground">
                  Gere a URL, autorize no Google e o código será capturado
                  automaticamente pela página de callback.
                </p>
              </div>
              <div className="flex flex-col md:flex-row gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleGenerateGoogleMeetAuthUrl}
                  disabled={generatingGoogleMeetAuthUrl}
                >
                  {generatingGoogleMeetAuthUrl ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Gerando URL...
                    </>
                  ) : (
                    'Gerar URL de autorização'
                  )}
                </Button>
              </div>
              <div className="flex flex-col md:flex-row gap-2">
                <Input
                  value={googleMeetConfig.authCode}
                  onChange={e =>
                    setGoogleMeetConfig(prev => ({
                      ...prev,
                      authCode: e.target.value
                    }))
                  }
                  placeholder="Cole aqui o código de autorização do Google"
                  autoComplete="off"
                />
                <Button
                  type="button"
                  onClick={handleExchangeGoogleMeetCode}
                  disabled={connectingGoogleMeet}
                >
                  {connectingGoogleMeet ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Conectando...
                    </>
                  ) : (
                    'Conectar Google'
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}

        <div className="flex justify-end gap-2 pt-4 border-t">
          <Button
            onClick={handleSaveGoogleMeetConfig}
            disabled={savingGoogleMeetConfig || loadingGoogleMeetConfig}
          >
            {savingGoogleMeetConfig ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Salvando...
              </>
            ) : (
              'Salvar Configuração do Meet'
            )}
          </Button>
        </div>
      </div>
    </Card>
  )
}
